import AnonymousInteractionGate from '@/components/AnonymousInteractionGate'
import DevTools from '@/components/DevTools'
import GlobalComponents from '@/components/GlobalComponents'
import GlobalServices from '@/components/GlobalServices'
import Layout from '@/components/Layout'
import ThemeService from '@/components/ThemeService'
import StoryViewerProvider from '@/components/Story/viewer/StoryViewerProvider'
import { checkBrowserEnv, checkMaliciousExtension } from '@/utils/env'
import {
	LIKE_LOTTIE_DATA_QUERY_OPTIONS,
	MANIFEST_QUERY_OPTIONS,
	VIDEO_UPLOAD_LIMIT_QUERY_OPTIONS,
} from '@/utils/fetchers/manifest'
import { currentUserQueryOptions } from '@/utils/fetchers/user'
import { clearUserSession } from '@/utils/navigate'
import queryClient from '@/utils/queryClient'
import storage from '@/utils/Storage'
import { configTracker } from '@/utils/tracker'
import { ModalsProvider } from '@mantine/modals'
import { Outlet, createRootRouteWithContext, redirect, useLocation } from '@tanstack/react-router'

const PUBLIC_DETAIL_PATHNAME_RE = /^\/u\/[^/]+\/(?:post|repost)\/[^/]+(?:\/[^/]+)?\/?$/

const isPublicDetailPathname = (pathname: string) => PUBLIC_DETAIL_PATHNAME_RE.test(pathname)

export const Route = createRootRouteWithContext()({
	component: Root,
	loader: async ({ location, route }) => {
		const isUnsupportedRoute = location.pathname === '/unsupported'
		const isAbnormalRoute = location.pathname === '/abnormal-environment'
		const isLoginRoute = location.pathname.startsWith('/login')

		const isSupported = checkBrowserEnv()

		if (!isSupported) {
			if (!isUnsupportedRoute) {
				throw redirect({
					to: '/unsupported',
				})
			}
			return
		}

		if (!isAbnormalRoute && !isLoginRoute && !isUnsupportedRoute) {
			const isMaliciousDetected = checkMaliciousExtension()
			if (isMaliciousDetected) {
				clearUserSession()
				throw redirect({
					to: '/abnormal-environment',
				})
			}
		}

		// 被支持的环境
		// 如果处于是未被支持页面，则跳转到首页
		if (isUnsupportedRoute) {
			throw redirect({
				to: '/',
			})
		}

		const basicQueries: Promise<unknown>[] = [queryClient.ensureQueryData(MANIFEST_QUERY_OPTIONS)]

		const isAnonymousDetailRoute = isPublicDetailPathname(location.pathname) && !storage.auth.token
		const isAuthedRoute = !isLoginRoute && !isUnsupportedRoute && !isAnonymousDetailRoute
		if (isAuthedRoute) {
			basicQueries.push(
				queryClient.ensureQueryData(currentUserQueryOptions).then((user) => {
					configTracker({
						distinctId: user.id,
					})
				}),
				queryClient.ensureQueryData(VIDEO_UPLOAD_LIMIT_QUERY_OPTIONS)
			)
		}

		queryClient.ensureQueryData(LIKE_LOTTIE_DATA_QUERY_OPTIONS)
		await Promise.all(basicQueries)
	},
})

function Root() {
	const pathname = useLocation({
		select: (location) => location.pathname,
	})
	const noNeedLayoutRoutes =
		pathname.startsWith('/login') || ['/unsupported', '/abnormal-environment'].includes(pathname)
	const isAnonymousDetailRoute = isPublicDetailPathname(pathname) && !storage.auth.token

	return (
		<>
			<ModalsProvider>
				{noNeedLayoutRoutes ? (
					<Outlet />
				) : (
					<StoryViewerProvider>
						<Layout>
							<Outlet />
						</Layout>
					</StoryViewerProvider>
				)}
				{isAnonymousDetailRoute && <AnonymousInteractionGate />}
			</ModalsProvider>
			<DevTools />
			<GlobalComponents />
			<GlobalServices isNotificationServiceEnabled={!isAnonymousDetailRoute} />
			<ThemeService />
		</>
	)
}
